import React, { useState, useEffect, useRef } from "react"
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Badge,
  Alert,
  Progress,
  Table,
  Collapse,
} from "reactstrap"
import {
  Activity,
  Heart,
  Play,
  Square,
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Hand,
  Timer,
  Edit3,
  Award,
  Bluetooth,
  BluetoothOff,
  Wind,
} from "lucide-react"
import axios from "axios"
import BLEManager from "./BLEManager"
import PPGVisualizer from "./PPGVisualizer"
import BreathingExercise from "components/Common/BreathingExercise"

const SCENARIO = {
  name: "Breathing PPG",
  description: "One-time PPG collection while tracking breathing.",
  color: "info",
  icon: Wind,
  minSessions: 1,
  estimatedDuration: 1,
  steps: [
    {
      id: 1,
      instruction:
        "Place the pulse oximeter on your LEFT middle finger and prepare to follow the breathing patterns on screen",
      duration: 15,
      type: "preparation",
      finger: "left",
      hand: "Left",
    },
    {
      id: 2,
      instruction: "Collecting PPG data from LEFT middle finger",
      duration: 60,
      type: "ppg",
      finger: "left",
      hand: "Left",
      noCuff: true,
    },
  ],
}

const BreathingPPGDataCollection = ({ selectedSubject, baseUrl, onBack }) => {
  // State management
  const [dashboardData, setDashboardData] = useState(null)
  const [previousStats, setPreviousStats] = useState([])
  const [currentBreathingSessionId, setCurrentBreathingSessionId] =
    useState(null)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [isCollecting, setIsCollecting] = useState(false)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  // BLE and data collection
  const [ppgData, setPpgData] = useState([])
  const [ppgRawData, setPpgRawData] = useState([])
  const [bleConnected, setBleConnected] = useState(false)
  const [bleDeviceName, setBleDeviceName] = useState(null)
  const collectedDataRef = useRef([])
  const collectedTimestampsRef = useRef([])
  const stepStartTimeRef = useRef(null)
  const bleReconnectingRef = useRef(false)
  const pendingGroundTruthRef = useRef(null)
  const visualizerKeyRef = useRef(0)

  // UI states
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showBPModal, setShowBPModal] = useState(false)
  const [bpValues, setBpValues] = useState({
    systolic: "",
    diastolic: "",
    heartRate: "",
  })
  const [bpErrors, setBpErrors] = useState([])
  const [isSessionExpanded, setSessionExpanded] = useState(false)

  // Breathing
  const [breathingDurations, setBreathingDurations] = useState([])
  const currentStep = SCENARIO.steps[currentStepIndex]
  const currentBreathingDurations =
    currentStep.type === "ppg" ? breathingDurations : null

  // Setup BLE event listeners
  useEffect(() => {
    BLEManager.on("disconnect", handleBLEDisconnect)

    return () => {
      BLEManager.off("disconnect", handleBLEDisconnect)
      if (BLEManager.isCollecting) {
        BLEManager.stopDataCollection()
      }
      if (BLEManager.device?.gatt?.connected) {
        BLEManager.disconnect()
      }
    }
  }, [])

  const handleBLEDisconnect = () => {
    console.warn("BLE device disconnected")
    setBleConnected(false)
    setBleDeviceName(null)

    if (isCollecting && !bleReconnectingRef.current) {
      setError("BLE device disconnected. Please reconnect to continue.")
    }
  }

  const handleReconnectBLE = async () => {
    try {
      bleReconnectingRef.current = true
      setLoading(true)
      setError(null)

      const connected = await BLEManager.connect()
      if (!connected) {
        throw new Error("Failed to reconnect to BLE device")
      }

      setBleConnected(true)
      setBleDeviceName(BLEManager.device?.name || "Unknown Device")
      setSuccess("BLE device reconnected successfully!")

      if (isPaused) {
        setIsPaused(false)
      }

      if (
        isCollecting &&
        (currentStep?.type === "ppg" || currentStep?.type === "simultaneous")
      ) {
        try {
          await startBLECollection()
        } catch (err) {
          console.error("Failed to start collection after reconnect:", err)
          setError(
            "Connected but failed to start data collection: " + err.message
          )
        }
      }

      bleReconnectingRef.current = false
      setLoading(false)
    } catch (err) {
      setError("Failed to reconnect BLE: " + err.message)
      bleReconnectingRef.current = false
      setLoading(false)
    }
  }

  useEffect(() => {
    let interval
    if (isCollecting && !isPaused) {
      interval = setInterval(() => {
        setElapsedTime(prev => {
          if (currentStep && prev >= currentStep.duration - 1) {
            handleStepComplete()
            return 0
          }
          return prev + 1
        })
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [isCollecting, isPaused, currentStepIndex])

  // cancel measurement if ble disconnected while collecting
  useEffect(() => {
    console.log('[session] cancel effect - connected:', bleConnected, "collecting:", isCollecting);
    if (!bleConnected && isCollecting) {
      console.log("[session] cancelling");
      setError("BLE disconnected - please reconnect and start over")
      setCurrentBreathingSessionId(null)
      setIsCollecting(false)
    }
  }, [bleConnected, isCollecting])

  const handleStartSession = async () => {
    try {
      setLoading(true)

      const connected = await BLEManager.connect()
      if (!connected) {
        throw new Error("Failed to connect to BLE device")
      }

      const response = await axios.post(`${baseUrl}web/breathing/setup`, {
        patientId: selectedSubject.subjectId,
      })

      const breathingDurations = intervalsToDurations(
        response.data.breathingTimeSignal
      )

      setBreathingDurations(breathingDurations)
      setCurrentBreathingSessionId(response.data.sessionId)

      setBleConnected(true)
      setBleDeviceName(BLEManager.device?.name || "Unknown Device")

      setCurrentStepIndex(0)
      setElapsedTime(0)
      setPpgData([])
      setPpgRawData([])
      collectedDataRef.current = []
      collectedTimestampsRef.current = []
      setIsCollecting(true)
      setError(null)
      setSuccess(null)

      setLoading(false)
    } catch (err) {
      setError("Failed to start session: " + err.message)
      setBleConnected(false)
      setLoading(false)
    }
  }

  const startBLECollection = async () => {
    if (!BLEManager.device?.gatt?.connected) {
      throw new Error("BLE device not connected")
    }

    console.log("===== STARTING NEW BLE COLLECTION =====")
    console.log("Collection Key:", visualizerKeyRef.current)

    visualizerKeyRef.current += 1
    console.log("New Visualizer key:", visualizerKeyRef.current)

    setPpgRawData([])
    setPpgData([])
    collectedDataRef.current = []
    collectedTimestampsRef.current = []

    console.log("Starting data collection...")
    console.log("BLE Manager state before start:", {
      allRawDataLength: BLEManager.allRawData?.length || 0,
      allTimestampsLength: BLEManager.allTimestamps?.length || 0,
      isCollecting: BLEManager.isCollecting,
    })

    const startTime = await BLEManager.startDataCollection(data => {
      setPpgRawData(prev => {
        const newData = [...prev, ...data]
        return newData
      })

      const timestamp = Date.now()

      const newDataPoints = data.map((value, idx) => ({
        timestamp: timestamp + idx,
        value: value,
        finger: currentStep?.finger,
      }))

      setPpgData(prev => [...prev.slice(-200), ...newDataPoints])
    })

    stepStartTimeRef.current = startTime
    console.log("BLE data collection started successfully at:", startTime)
    console.log("=========================================")
  }

  const stopBLECollection = async () => {
    if (!BLEManager.isCollecting) {
      return { rawData: [], timestamps: [] }
    }

    console.log("Stopping BLE collection")
    const collectionData = await BLEManager.stopDataCollection()

    if (collectionData) {
      console.log("Disconnecting BLE to reset state...")
      try {
        setIsCollecting(false)
        await BLEManager.disconnect()
        console.log("BLE disconnected successfully")

        await new Promise(resolve => setTimeout(resolve, 500))

        console.log("Reconnecting BLE...")
        const reconnected = await BLEManager.connect()
        if (reconnected) {
          console.log("BLE reconnected successfully")
          setBleConnected(true)
          setBleDeviceName(BLEManager.device?.name || "Unknown Device")
        } else {
          console.warn("Failed to reconnect BLE")
          setBleConnected(false)
        }
      } catch (err) {
        console.error("Error during BLE disconnect/reconnect:", err)
        setBleConnected(false)
      }

      return {
        rawData: collectionData.rawData,
        timestamps: collectionData.timestamps,
        startTime: collectionData.startTime,
        endTime: collectionData.endTime,
      }
    }

    return { rawData: [], timestamps: [] }
  }

  const base64EncodePPGData = ppgData => {
    return btoa(String.fromCharCode.apply(null, ppgData))
  }

  const saveBreathingPPGData = async ppgData => {
    const encodedSignal = base64EncodePPGData(ppgData.rawData)

    const payload = {
      sessionId: currentBreathingSessionId,
      deviceData: {
        collectedAt: ppgData.collectedAt,
        signal: encodedSignal,
        bleDeviceId: ppgData.bleDeviceId,
        hand: ppgData.hand,
        timestamps: ppgData.timestamps,
        groundTruth: null,
      },
    }

    await axios.post(`${baseUrl}web/breathing/save`, payload)

    console.log("Breathing PPG data saved:", {
      dataPoints: ppgData.rawData.length,
      hand: ppgData.hand,
      bleDeviceId: ppgData.bleDeviceId,
      noCuff: true,
    })
  }

  const handleStepComplete = async () => {
    console.log("Step Complete - Current Step:", currentStep)

    if (currentStep?.type === "preparation") {
      console.log("Preparation step complete, moving to next")
      moveToNextStep()
      return
    }

    if (currentStep?.type === "ppg") {
      console.log("PPG step - stopping collection")

      const currentBleDeviceName =
        BLEManager.device?.name || bleDeviceName || "Unknown"
      console.log("BLE Device Name captured:", currentBleDeviceName)

      const bleData = await stopBLECollection()

      collectedDataRef.current = bleData.rawData
      collectedTimestampsRef.current = bleData.timestamps

      const ppgDataToSave = {
        rawData: bleData.rawData,
        timestamps: bleData.timestamps,
        hand: currentStep.hand,
        collectedAt: Math.floor(Date.now() / 1000),
        bleDeviceId: currentBleDeviceName,
      }

      // Save data
      try {
        setLoading(true)
        await saveBreathingPPGData(ppgDataToSave)

        collectedDataRef.current = []
        collectedTimestampsRef.current = []
        setPpgData([])
        setPpgRawData([])

        setLoading(false)

        await moveToNextStep()
      } catch (err) {
        console.error("Error saving NoCuff data:", err)
        setError(
          "Failed to save data: " + (err.response?.data?.message || err.message)
        )
        setLoading(false)
      }
    }

    console.log("Other step type, moving to next")
    moveToNextStep()
  }

  const moveToNextStep = async () => {
    if (currentStepIndex < SCENARIO.steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1)
      setElapsedTime(0)

      const nextStep = SCENARIO.steps[currentStepIndex + 1]
      console.log("Next step:", nextStep)
      if (nextStep.type === "ppg" || nextStep.type === "simultaneous") {
        if (!BLEManager.device?.gatt?.connected) {
          console.log(
            "BLE device not connected. Waiting for manual reconnection..."
          )
          setError(
            "Please reconnect BLE device to continue with PPG collection"
          )
          setIsPaused(true)
          return
        }

        try {
          await startBLECollection()
        } catch (err) {
          console.error("Failed to start BLE collection:", err)
          setError(
            "Failed to start BLE collection. Please reconnect the device."
          )
          setIsPaused(true)
        }
      }
    } else {
      await handleSessionComplete()
    }
  }

  const handleSessionComplete = async () => {
    setIsCollecting(false)

    if (BLEManager.isCollecting) {
      await BLEManager.stopDataCollection()
    }

    try {
      setCurrentBreathingSessionId(null)
      setCurrentStepIndex(0)
      setSuccess("Session completed successfully!")
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to end session"
      setError(errorMessage)
      console.error("Error ending session:", err)
    }
  }

  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  const formatTimestamp = timestamp => {
    const date = new Date(timestamp * 1000)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStepTypeIcon = type => {
    switch (type) {
      case "preparation":
        return <Hand size={16} />
      case "ppg":
        return <Activity size={16} />
      case "simultaneous":
        return <Activity size={16} />
      case "bp":
        return <Heart size={16} />
      case "break":
        return <Timer size={16} />
      case "instruction":
        return <Edit3 size={16} />
      default:
        return <Clock size={16} />
    }
  }

  const getStepTypeColor = type => {
    switch (type) {
      case "preparation":
        return "info"
      case "ppg":
        return "success"
      case "simultaneous":
        return "success"
      case "bp":
        return "danger"
      case "break":
        return "secondary"
      case "instruction":
        return "warning"
      default:
        return "primary"
    }
  }

  const formatDuration = seconds => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins > 0 && secs > 0) {
      return `${mins}m ${secs}s`
    } else if (mins > 0) {
      return `${mins}m`
    } else {
      return `${secs}s`
    }
  }

  const getTotalDuration = steps => {
    return steps.reduce((total, step) => total + step.duration, 0)
  }

  const getProgressPercentage = () => {
    const steps = SCENARIO.steps
    return ((currentStepIndex + 1) / steps.length) * 100
  }

  const intervalsToDurations = intervals => {
    if (!intervals || intervals.length === 0) return null
    const durations = [intervals[0]]
    for (let i = 1; i < intervals.length; i++) {
      const diff = intervals[i] - intervals[i - 1]
      durations.push(Number(diff.toFixed(2)))
    }
    return durations
  }

  if (loading && !dashboardData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading experiment data...</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Card
        className="border-0 shadow-sm mb-4"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <CardBody className="text-white">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center mb-2">
                <Wind size={32} className="me-3" />
                <div>
                  <h3 className="mb-1 text-white">
                    Breathing PPG Data Collection Experiment
                  </h3>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Badge
                color={bleConnected ? "success" : "secondary"}
                className="px-3 py-2 me-2"
              >
                {bleConnected ? (
                  <Bluetooth size={14} className="me-1" />
                ) : (
                  <BluetoothOff size={14} className="me-1" />
                )}
                {bleConnected
                  ? bleDeviceName || "BLE Connected"
                  : "BLE Disconnected"}
              </Badge>
              {!bleConnected && isCollecting && (
                <Button
                  color="warning"
                  size="sm"
                  onClick={handleReconnectBLE}
                  disabled={loading}
                >
                  <Bluetooth size={14} className="me-1" />
                  Reconnect
                </Button>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {error && (
        <Alert
          color="danger"
          className="d-flex align-items-center mb-4"
          toggle={() => setError(null)}
        >
          <AlertCircle size={20} className="me-2" />
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          color="success"
          className="d-flex align-items-center mb-4"
          toggle={() => setSuccess(null)}
        >
          <CheckCircle size={20} className="me-2" />
          {success}
        </Alert>
      )}

      {previousStats.length > 0 && (
        <Card className="border-0 shadow-sm mb-4">
          <CardBody>
            <div className="d-flex align-items-center mb-3">
              <Award size={20} className="text-primary me-2" />
              <h5 className="mb-0">Experiment History</h5>
            </div>
            <Row>
              {previousStats.map((stat, index) => (
                <Col md={4} key={index}>
                  <Card
                    className="border-0"
                    style={{ backgroundColor: "#f8f9fa" }}
                  >
                    <CardBody>
                      <div className="d-flex align-items-center mb-2">
                        {React.createElement(SCENARIO.icon, {
                          size: 24,
                          className: `text-${SCENARIO.color} me-2`,
                        })}
                        <h6 className="mb-0">{SCENARIO.name}</h6>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <Badge color={SCENARIO.color} pill className="px-3">
                          {stat.count} completed
                        </Badge>
                        <small className="text-muted">
                          {formatTimestamp(stat.lastTimeStarted)}
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>
      )}

      {currentBreathingSessionId && isCollecting && (
        <Row>
          <Col lg={10} className="mx-auto">
            <Card className="border-0 shadow-lg">
              <CardBody className="p-4">
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Session in Progress</h5>
                    <Badge color="warning" className="px-3 py-2">
                      <Clock size={14} className="me-1" />
                      Step {currentStepIndex + 1} of {SCENARIO.steps.length}
                    </Badge>
                  </div>
                  <Progress
                    value={getProgressPercentage()}
                    className="mb-2"
                    style={{ height: "8px" }}
                  >
                    <div
                      className="progress-bar"
                      style={{ width: `${getProgressPercentage()}%` }}
                    ></div>
                  </Progress>
                  <small className="text-muted">
                    {Math.round(getProgressPercentage())}% Complete
                  </small>
                </div>

                {currentStep && (
                  <div className="mb-4">
                    <Card
                      style={{
                        backgroundColor: "#f8f9fa",
                        border: `3px solid var(--bs-${getStepTypeColor(
                          currentStep.type
                        )})`,
                        borderRadius: "12px",
                      }}
                    >
                      <CardBody className="p-4">
                        <div className="d-flex align-items-start mb-4">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{
                              width: "60px",
                              height: "60px",
                              backgroundColor: `var(--bs-${getStepTypeColor(
                                currentStep.type
                              )})`,
                              color: "white",
                            }}
                          >
                            {getStepTypeIcon(currentStep.type)}
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <Badge
                                color={getStepTypeColor(currentStep.type)}
                                className="text-uppercase"
                              >
                                {currentStep.type}
                              </Badge>
                              {currentStep.finger && (
                                <Badge
                                  color="dark"
                                  style={{
                                    fontSize: "0.7rem",
                                    fontWeight: "bold",
                                    padding: "0.4rem 0.8rem",
                                  }}
                                >
                                  {currentStep.hand === "Left" ? "👈" : "👉"}{" "}
                                  {currentStep.hand.toUpperCase()} Hand
                                </Badge>
                              )}
                            </div>
                            <h4 className="mb-0" style={{ lineHeight: "1.5" }}>
                              {currentStep.instruction}
                            </h4>
                          </div>
                        </div>

                        <div className="row g-3">
                          {/* Timer Card */}
                          <div className="col-6">
                            <div
                              className="text-center p-3 bg-white shadow-sm h-100 d-flex align-items-center justify-content-center position-relative"
                              style={{ borderRadius: "20px" }}
                            >
                              <div
                                className={`position-relative d-inline-flex text-${getStepTypeColor(
                                  currentStep.type
                                )}`}
                              >
                                {/* SVG: 160px wide, Radius 70, Circumference ~440 */}
                                <svg
                                  width="160"
                                  height="160"
                                  style={{ transform: "rotate(-90deg)" }}
                                >
                                  <circle
                                    cx="80"
                                    cy="80"
                                    r="70"
                                    stroke="#e9ecef"
                                    strokeWidth="10"
                                    fill="none"
                                  />
                                  <circle
                                    cx="80"
                                    cy="80"
                                    r="70"
                                    stroke="currentColor"
                                    strokeWidth="10"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeDasharray="440"
                                    strokeDashoffset={
                                      currentStep.duration > 0
                                        ? 440 -
                                          (elapsedTime / currentStep.duration) *
                                            440
                                        : 440
                                    }
                                    style={{
                                      transition:
                                        "stroke-dashoffset 0.5s linear",
                                    }}
                                  />
                                </svg>

                                {/* Text Overlay */}
                                <div
                                  className="position-absolute top-50 start-50 translate-middle text-center text-dark"
                                  style={{ width: "100%" }}
                                >
                                  <small
                                    className="text-muted text-uppercase d-block"
                                    style={{
                                      fontSize: "0.7rem",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    Remaining
                                  </small>
                                  <h2
                                    className="font-monospace mb-0"
                                    style={{
                                      fontWeight: "300",
                                      color: "#2c3e50",
                                      fontSize: "2.5rem",
                                    }}
                                  >
                                    {formatTime(
                                      currentStep.duration - elapsedTime
                                    )}
                                  </h2>
                                  <small
                                    className="text-muted"
                                    style={{ fontSize: "0.8rem" }}
                                  >
                                    of {formatTime(currentStep.duration)}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Right Col: Breathing Exercise */}
                          <div className="col-6">
                            <div
                              className="p-3 bg-white shadow-sm h-100 d-flex align-items-center justify-content-center"
                              style={{ borderRadius: "20px" }}
                            >
                              <BreathingExercise
                                breathParameters={currentBreathingDurations}
                                isPaused={isPaused}
                                firstStepDuration={breathingDurations?.[0] || 0}
                                key={currentStep.type}
                              />
                            </div>
                          </div>
                        </div>
                        {(currentStep.type === "ppg" ||
                          currentStep.type === "simultaneous") && (
                          <div className="mt-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div>
                                <strong className="text-success">
                                  PPG Signal Monitor
                                </strong>
                                <small className="text-muted d-block">
                                  Real-time data from BLE device
                                </small>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {!bleConnected && (
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={handleReconnectBLE}
                                    disabled={loading}
                                  >
                                    <Bluetooth size={14} className="me-1" />
                                    Reconnect
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div
                              className="bg-dark rounded p-3 shadow-lg"
                              style={{ height: "300px" }}
                            >
                              <PPGVisualizer
                                key={visualizerKeyRef.current}
                                rawData={ppgRawData}
                                showAll={false}
                              />
                            </div>
                          </div>
                        )}

                        {currentStep.type !== "ppg" &&
                          currentStep.type !== "simultaneous" &&
                          currentStep.type !== "break" && (
                            <Row className="mt-4">
                              <Col>
                                <Button
                                  color="warning"
                                  onClick={() => setIsPaused(!isPaused)}
                                  block
                                  size="lg"
                                  disabled={loading}
                                >
                                  {isPaused ? (
                                    <Play size={20} />
                                  ) : (
                                    <Square size={20} />
                                  )}
                                  {isPaused ? " Resume" : " Pause"}
                                </Button>
                              </Col>
                              <Col>
                                <Button
                                  color="primary"
                                  onClick={handleStepComplete}
                                  block
                                  size="lg"
                                  disabled={loading}
                                >
                                  <CheckCircle size={20} className="me-2" />
                                  Complete Step
                                </Button>
                              </Col>
                            </Row>
                          )}

                        {(currentStep.type === "ppg" ||
                          currentStep.type === "simultaneous" ||
                          currentStep.type === "break") && (
                          <Alert color="info" className="mt-4 mb-0">
                            <Clock size={16} className="me-2" />
                            {currentStep.type === "break"
                              ? "Please remain still and relaxed. This step will automatically proceed when the timer reaches zero."
                              : "Please remain still and avoid movement. Data collection will complete automatically when the timer reaches zero."}
                          </Alert>
                        )}

                        {!bleConnected &&
                          (currentStep?.type === "ppg" ||
                            currentStep?.type === "simultaneous") && (
                            <Alert color="warning" className="mt-3 mb-0">
                              <AlertCircle size={16} className="me-2" />
                              <strong>BLE Device Disconnected!</strong> Please
                              reconnect the device to continue collecting data.
                            </Alert>
                          )}
                      </CardBody>
                    </Card>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      {!currentBreathingSessionId && (

      <div>
        <Card className="border-0 shadow-sm mb-4">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0">Session Management</h5>
            </div>

            <div className="sessions-list">
              <Card
                key={currentBreathingSessionId}
                className="mb-3 border-0 shadow-sm"
              >
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center flex-grow-1">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: "#d4edda",
                        }}
                      >
                        <Wind size={20} className="text-success" />
                      </div>
                      <strong>Breathing PPG session</strong>
                      <div className="ms-3">
                        <Badge color="light" className="ms-2">
                          <Clock size={12} className="me-1" />~
                          {SCENARIO.estimatedDuration} min
                        </Badge>
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => handleStartSession()}
                        disabled={loading}
                      >
                        <Play size={16} className="me-1" />
                        Start
                      </Button>
                      <Button
                        color="light"
                        size="sm"
                        onClick={() => setSessionExpanded(prev => !prev)}
                      >
                        {isSessionExpanded ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </Button>
                    </div>
                  </div>

                  <Collapse isOpen={isSessionExpanded}>
                    <div className="mt-3 pt-3 border-top">
                      <h6 className="mb-3">Protocol Steps:</h6>
                      <Table size="sm" className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th width="40"></th>
                            <th>Step</th>
                            <th>Instruction</th>
                            <th width="100" className="text-end">
                              Duration
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {SCENARIO.steps.map((step, stepIndex) => (
                            <tr key={stepIndex}>
                              <td width="40">
                                <div
                                  className={`text-${getStepTypeColor(
                                    step.type
                                  )}`}
                                >
                                  {getStepTypeIcon(step.type)}
                                </div>
                              </td>
                              <td>
                                <Badge
                                  color={getStepTypeColor(step.type)}
                                  className="text-uppercase"
                                >
                                  {step.type}
                                </Badge>
                                {step.hand && (
                                  <Badge color="dark" className="ms-1">
                                    {step.hand === "Left" ? "👈" : "👉"}{" "}
                                    {step.hand}
                                  </Badge>
                                )}
                              </td>
                              <td>{step.instruction}</td>
                              <td width="100" className="text-end">
                                <Badge color="light" className="font-monospace">
                                  {formatDuration(step.duration)}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-light">
                          <tr>
                            <td colSpan="3" className="text-end">
                              <strong>Total Duration:</strong>
                            </td>
                            <td className="text-end">
                              <Badge color="primary" className="font-monospace">
                                {formatDuration(
                                  getTotalDuration(SCENARIO.steps)
                                )}
                              </Badge>
                            </td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </Collapse>
                </CardBody>
              </Card>
            </div>
          </CardBody>
        </Card>
      </div>
      )}
      <style>{`
        .scenario-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.15) !important;
        }
        .sessions-list {
          max-height: 800px;
          overflow-y: auto;
        }
      `}</style>
    </div>
  )
}

export default BreathingPPGDataCollection
